import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  MailIcon,
  ChatIcon,
  CheckCircleIcon,
  HelpCircleIcon,
  LogoIcon,
  ArrowLeftIcon,
  ExternalLinkIcon,
  SparklesIcon,
} from '../icons/icons';

const Contact = () => {
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: 'general',
    message: '',
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [openFaq, setOpenFaq] = useState(0);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;

    setIsSubmitting(true);
    // Simulate instant network submission
    setTimeout(() => {
      setIsSubmitting(false);
      setIsSubmitted(true);
    }, 800);
  };

  const handleReset = () => {
    setFormData({
      name: '',
      email: '',
      subject: 'general',
      message: '',
    });
    setIsSubmitted(false);
  };

  const faqs = [
    {
      q: 'How frequently are market prices and statistics updated?',
      a: 'Market data, pricing, trading volumes, and global metrics are streamed dynamically through the CoinGecko public API v3 with intelligent in-memory caching to guarantee real-time clarity and sub-second rendering.',
    },
    {
      q: 'Is CryptoPulse completely free to use?',
      a: 'Yes, 100% free and open-source. There are no paywalls, hidden tiers, or subscriptions required to access full market terminal capabilities, charts, or multi-currency conversions.',
    },
    {
      q: 'How does the Watchlist work?',
      a: 'Your watchlist is saved securely in your browser’s local storage. No account registration or wallet connection is necessary, keeping your tracking private and local to your device.',
    },
    {
      q: 'Can I contribute or report a bug?',
      a: 'Absolutely! CryptoPulse is open-source. You can submit pull requests, open issues, or request new features on our official GitHub repository.',
    },
  ];

  return (
    <main className="wrapper-container py-8 space-y-12">
      {/* Top Back Breadcrumb */}
      <div className="flex items-center justify-between">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-xs font-semibold text-slate-400 hover:text-cyan-400 transition-colors px-3.5 py-2 rounded-xl bg-slate-900 border border-slate-800"
        >
          <ArrowLeftIcon className="w-4 h-4" />
          <span>Back to Markets</span>
        </Link>

        <div className="flex items-center gap-2 text-xs font-semibold text-emerald-400 bg-emerald-500/10 border border-emerald-500/20 px-3 py-1.5 rounded-xl">
          <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
          <span>Support Desk Online</span>
        </div>
      </div>

      {/* Hero Showcase Banner */}
      <section className="relative overflow-hidden glass-card p-8 md:p-12 border-slate-800/80 bg-gradient-to-br from-dark-900/90 via-dark-850/80 to-slate-900/90">
        <div className="absolute -top-28 -right-28 w-80 h-80 bg-cyan-500/15 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute -bottom-28 -left-28 w-80 h-80 bg-indigo-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="relative z-10 max-w-3xl space-y-4">
          <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-cyan-400 text-xs font-bold uppercase tracking-wider">
            <MailIcon className="w-4 h-4" />
            <span>Connect with CryptoPulse</span>
          </div>

          <h1 className="text-3xl sm:text-5xl font-black text-slate-100 tracking-tight leading-tight">
            We’d Love to <span className="text-gradient-cyan">Hear from You</span>
          </h1>

          <p className="text-slate-300 text-base sm:text-lg leading-relaxed">
            Have a question, feedback, partnership inquiry, or feature request? Reach out to our team or explore our community channels below.
          </p>
        </div>
      </section>

      {/* Main Grid: Form + Info Cards */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Contact Form Column */}
        <div className="lg:col-span-7">
          <div className="glass-card p-6 sm:p-8 border-slate-800 relative overflow-hidden">
            <h2 className="text-xl font-bold text-slate-100 mb-2 flex items-center gap-2">
              <SparklesIcon className="w-5 h-5 text-cyan-400" />
              <span>Send Us a Message</span>
            </h2>
            <p className="text-xs text-slate-400 mb-6">
              Fill out the form below and we’ll get back to you within 24 hours.
            </p>

            {isSubmitted ? (
              <div className="p-8 text-center space-y-4 bg-slate-900/80 border border-emerald-500/30 rounded-2xl animate-fade-in">
                <div className="w-14 h-14 bg-emerald-500/10 border border-emerald-500/20 rounded-full flex items-center justify-center mx-auto text-emerald-400">
                  <CheckCircleIcon className="w-8 h-8" />
                </div>
                <h3 className="text-lg font-bold text-slate-100">Message Received!</h3>
                <p className="text-xs text-slate-400 max-w-md mx-auto leading-relaxed">
                  Thank you, <strong className="text-slate-200">{formData.name}</strong>. Your message regarding{' '}
                  <span className="text-cyan-400 font-medium">"{formData.subject}"</span> has been dispatched to our engineering and support desk.
                </p>
                <button
                  onClick={handleReset}
                  className="mt-2 px-5 py-2.5 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-bold text-xs transition-all shadow-lg shadow-cyan-500/20"
                >
                  Send Another Message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {/* Name Input */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-300 block">
                      Your Name <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      placeholder="e.g. Satoshi Nakamoto"
                      value={formData.name}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-slate-900/90 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/20 transition-all font-medium"
                    />
                  </div>

                  {/* Email Input */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-semibold text-slate-300 block">
                      Email Address <span className="text-cyan-400">*</span>
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      placeholder="name@domain.com"
                      value={formData.email}
                      onChange={handleChange}
                      className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-slate-900/90 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/20 transition-all font-medium"
                    />
                  </div>
                </div>

                {/* Subject Selector */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300 block">
                    Inquiry Topic
                  </label>
                  <div className="relative">
                    <select
                      name="subject"
                      value={formData.subject}
                      onChange={handleChange}
                      className="w-full appearance-none px-3.5 py-2.5 text-xs rounded-xl bg-slate-900/90 border border-slate-800 text-slate-200 cursor-pointer focus:outline-none focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/20 transition-all font-medium"
                    >
                      <option value="general" className="bg-dark-900 text-slate-100">
                        💬 General Inquiry / Feedback
                      </option>
                      <option value="api" className="bg-dark-900 text-slate-100">
                        ⚡ API & Developer Integration
                      </option>
                      <option value="bug" className="bg-dark-900 text-slate-100">
                        🐛 Bug Report / Technical Issue
                      </option>
                      <option value="feature" className="bg-dark-900 text-slate-100">
                        💡 Feature Request / Proposal
                      </option>
                      <option value="partnership" className="bg-dark-900 text-slate-100">
                        🤝 Partnership / Token Listing
                      </option>
                    </select>
                    <div className="absolute right-3.5 top-1/2 -translate-y-1/2 pointer-events-none text-slate-500 text-[10px]">
                      ▼
                    </div>
                  </div>
                </div>

                {/* Message Textarea */}
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-300 block">
                    Message <span className="text-cyan-400">*</span>
                  </label>
                  <textarea
                    name="message"
                    required
                    rows={5}
                    placeholder="Tell us what you'd like to discuss or share..."
                    value={formData.message}
                    onChange={handleChange}
                    className="w-full px-3.5 py-2.5 text-xs rounded-xl bg-slate-900/90 border border-slate-800 text-slate-100 placeholder-slate-500 focus:outline-none focus:border-cyan-500/60 focus:ring-2 focus:ring-cyan-500/20 transition-all font-medium resize-none leading-relaxed"
                  />
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-3 px-5 rounded-xl bg-gradient-to-r from-cyan-500 to-emerald-500 hover:from-cyan-400 hover:to-emerald-400 text-slate-950 font-extrabold text-xs transition-all duration-200 shadow-lg shadow-cyan-500/25 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-50"
                >
                  {isSubmitting ? (
                    <span className="inline-flex items-center gap-2">
                      <span className="w-4 h-4 border-2 border-slate-950 border-t-transparent rounded-full animate-spin"></span>
                      Dispatching Message...
                    </span>
                  ) : (
                    <span>Send Message 🚀</span>
                  )}
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Contact Information & Channels Column */}
        <div className="lg:col-span-5 space-y-4">
          {/* Direct Email Card */}
          <div className="glass-card p-5 border-slate-800 space-y-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-cyan-500/10 border border-cyan-500/20 text-cyan-400">
                <MailIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-100">Direct Email</h3>
                <p className="text-[11px] text-slate-400">For general support & inquiries</p>
              </div>
            </div>
            <div className="pt-2">
              <a
                href="mailto:support@cryptopulse.io"
                className="text-xs font-mono font-semibold text-cyan-400 hover:text-cyan-300 transition-colors block"
              >
                support@cryptopulse.io
              </a>
            </div>
          </div>

          {/* Community & Chat Card */}
          <div className="glass-card p-5 border-slate-800 space-y-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-emerald-500/10 border border-emerald-500/20 text-emerald-400">
                <ChatIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-100">Community Channels</h3>
                <p className="text-[11px] text-slate-400">Join our developer & trader circles</p>
              </div>
            </div>
            <div className="pt-2 flex items-center gap-3">
              <a
                href="https://discord.com"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 hover:text-cyan-400 bg-slate-900 border border-slate-800 px-3 py-1.5 rounded-lg transition-colors"
              >
                <span>Discord</span>
                <ExternalLinkIcon className="w-3 h-3" />
              </a>
              <a
                href="https://telegram.org"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-xs font-semibold text-slate-300 hover:text-cyan-400 bg-slate-900 border border-slate-800 px-3 py-1.5 rounded-lg transition-colors"
              >
                <span>Telegram</span>
                <ExternalLinkIcon className="w-3 h-3" />
              </a>
            </div>
          </div>

          {/* GitHub Open Source Card */}
          <div className="glass-card p-5 border-slate-800 space-y-2">
            <div className="flex items-center gap-3">
              <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
                <LogoIcon className="w-5 h-5" />
              </div>
              <div>
                <h3 className="text-sm font-bold text-slate-100">Open Source Repository</h3>
                <p className="text-[11px] text-slate-400">Inspect source code or file issues</p>
              </div>
            </div>
            <div className="pt-2">
              <a
                href="https://github.com/hemantmm/CryptoMarket"
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-200 hover:text-cyan-400 bg-slate-900 border border-slate-800 px-3.5 py-1.5 rounded-xl transition-colors"
              >
                <span>github.com/hemantmm/CryptoMarket</span>
                <ExternalLinkIcon className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>

          {/* Operational Hours / SLA Card */}
          <div className="glass-card p-5 border-slate-800 space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-400">Average Response SLA:</span>
              <span className="font-bold text-slate-200 font-mono">&lt; 24 Hours</span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-slate-400">Status:</span>
              <span className="text-emerald-400 font-semibold flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
                Operational 24/7
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Frequently Asked Questions (FAQ) Accordion */}
      <section className="glass-card p-8 border-slate-800 space-y-6">
        <div className="flex items-center gap-2.5 border-b border-slate-800/80 pb-4">
          <HelpCircleIcon className="w-6 h-6 text-cyan-400" />
          <div>
            <h2 className="text-xl font-bold text-slate-100">Frequently Asked Questions</h2>
            <p className="text-xs text-slate-400 mt-0.5">Quick answers to common questions</p>
          </div>
        </div>

        <div className="space-y-3">
          {faqs.map((faq, idx) => (
            <div
              key={idx}
              className="bg-slate-900/60 border border-slate-800/80 rounded-xl overflow-hidden transition-all"
            >
              <button
                onClick={() => setOpenFaq(openFaq === idx ? -1 : idx)}
                className="w-full p-4 text-left flex items-center justify-between gap-4 text-xs sm:text-sm font-bold text-slate-200 hover:text-cyan-400 transition-colors"
              >
                <span>{faq.q}</span>
                <span className="text-slate-400 text-xs font-mono font-normal">
                  {openFaq === idx ? '▲' : '▼'}
                </span>
              </button>
              {openFaq === idx && (
                <div className="px-4 pb-4 text-xs text-slate-400 leading-relaxed border-t border-slate-800/50 pt-3">
                  {faq.a}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </main>
  );
};

export default Contact;
